import { AppPage } from "./AppPage";

export interface AppPathDescription {
  absolute: string;
  relative: string;
  params?: string[];
  postfix?: string;
  path: string;
}

export type AppPathRoutes<T extends string> = {
  [path in T]: AppPathDescription;
};

export const buildAppRoute = (
  relative: string,
  postfix?: string,
  params?: string[],
): AppPathDescription => ({
  absolute: `${postfix ?? ""}${relative}`,
  relative,
  params,
  postfix,
  path: relative,
});

export const REPORT_PREFIX = "rapport";
export const FORM_PREFIX = "utfylling";
export const PAGE_TWO_PREFIX = "side-to";

export const AppRoutes: AppPathRoutes<AppPage> = {
  [AppPage.LandingPage]: buildAppRoute("/"),
  [AppPage.AboutLandingPage]: buildAppRoute("om", "/"),
  [AppPage.AboutDigitalSigning]: buildAppRoute("digital-signering", "/om/"),
  [AppPage.AboutFaq]: buildAppRoute("sporsmal-og-svar", "/om/"),
  [AppPage.AboutPrivacy]: buildAppRoute("cookies-og-personvern", "/om/"),
  [AppPage.AboutTerms]: buildAppRoute("vilkar", "/om/"),
  [AppPage.Create]: buildAppRoute("opprett", "/"),
  [AppPage.Split]: buildAppRoute("split", "/stengt/"),
  [AppPage.Report]: buildAppRoute(REPORT_PREFIX, "/"),
  [AppPage.ReportLoginCallback]: buildAppRoute(
    "innlogging",
    `/${REPORT_PREFIX}/`,
  ),
  [AppPage.ReportForm]: buildAppRoute(FORM_PREFIX, `/${REPORT_PREFIX}/`),
  [AppPage.ReportFormAccidentDescription]: buildAppRoute(
    "om-uhellet",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormVehicle]: buildAppRoute(
    "kjoretoy",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormDriver]: buildAppRoute(
    "forer",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormInsurance]: buildAppRoute(
    "forsikring",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormAccident]: buildAppRoute(
    "hendelsen",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormSketch]: buildAppRoute(
    "tegning",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormAttachments]: buildAppRoute(
    "vedlegg",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportFormWitness]: buildAppRoute(
    "vitner",
    `/${REPORT_PREFIX}/${FORM_PREFIX}/`,
  ),
  [AppPage.ReportPreview]: buildAppRoute(
    "forhandsvisning",
    `/${REPORT_PREFIX}/`,
  ),
  [AppPage.ReportSign]: buildAppRoute("signer/:hash", `/${REPORT_PREFIX}/`, [
    ":hash",
  ]),
  [AppPage.ReportSignCallback]: buildAppRoute(
    "callback",
    `/${REPORT_PREFIX}/signer/:hash/`,
    [":hash"],
  ),
  [AppPage.ReportSignatureStatus]: buildAppRoute(
    "status",
    `/${REPORT_PREFIX}/signer/`,
  ),
  [AppPage.ReportConfirmation]: buildAppRoute(
    "bekreftelse",
    `/${REPORT_PREFIX}/`,
  ),
  [AppPage.ReportPageTwoSupplementalInfo]: buildAppRoute(
    "supplerende-info",
    `/${REPORT_PREFIX}/${PAGE_TWO_PREFIX}/`,
  ),
  [AppPage.ReportPageTwoAccidentDescription]: buildAppRoute(
    "beskrivelse",
    `/${REPORT_PREFIX}/${PAGE_TWO_PREFIX}/`,
  ),
  [AppPage.ReportPageTwoInjury]: buildAppRoute(
    "personskader",
    `/${REPORT_PREFIX}/${PAGE_TWO_PREFIX}/`,
  ),
};

export function getAbsolutePath(
  page: AppPage,
  paramValues?: { [key: string]: string },
) {
  const currentPage = AppRoutes[page];
  if (!AppRoutes[page]) {
    throw Error(`Could not find ${page}`);
  }
  const { params = [], absolute } = currentPage;

  const absolutePath = params.reduce((ap, paramKey) => {
    if (paramValues?.[paramKey]) {
      return ap.replace(paramKey, paramValues[paramKey]);
    }
    return ap;
  }, absolute);

  return absolutePath;
}

export function getRelativePath(page: AppPage) {
  return AppRoutes[page].relative;
}

export function getChildrenPaths(
  path: string,
): Partial<AppPathRoutes<AppPage>> {
  const objectKeys = Object.keys(AppRoutes);

  return objectKeys.reduce(
    (op, pathName) => {
      const appPage = AppRoutes[pathName as AppPage];
      if (appPage.absolute.startsWith(path) && appPage.absolute !== path) {
        op[pathName as AppPage] = appPage;
      }
      return op;
    },
    {} as Record<AppPage, AppPathDescription>,
  );
}
